// « Situations de crise »: what the dialer does when things go sideways. The durations and limits come from the
// dialer code (pause cap, offline outbox size, 24 h edit window, 30 min orphan rule); crisis.test.ts checks them
// against the code and the SQL.
import { EDIT_WINDOW_MS } from "../../dialer/callEditWindow";
import { OUTBOX_MAX } from "../../dialer/session/outbox";
import { PAUSE_CAP_MS } from "../../dialer/session/useLeaseRenewal";
import type { QuizQuestion } from "./types";

const K = "gavion.training.content.crisis";

/** Orphan calls become « Non consigné » after this many minutes (SQL: gavion-automation-tick, 22_…_automation.sql). */
export const UNLOGGED_AFTER_MINUTES = 30;

const question = (
  id: string,
  answer: string,
  others: readonly string[],
  explanationArgs: QuizQuestion["explanationArgs"] = {},
  promptArgs: QuizQuestion["promptArgs"] = {},
): QuizQuestion => ({
  id,
  promptKey: `${K}.${id}.prompt`,
  promptArgs,
  // the right answer is not always first
  options: [answer, ...others]
    .sort()
    .map((o) => ({ id: o, labelKey: `${K}.${id}.${o}` })),
  answer,
  explanationKey: `${K}.${id}.explanation`,
  explanationArgs,
});

const PAUSE_MINUTES = PAUSE_CAP_MS / 60_000;

export const CRISIS_QUESTIONS: readonly QuizQuestion[] = [
  question(
    "long_pause",
    "released",
    ["kept", "session_ends"],
    { minutes: PAUSE_MINUTES },
    { minutes: PAUSE_MINUTES + 2 },
  ),
  question("offline", "queued", ["lost", "queue_moves"], { max: OUTBOX_MAX }),
  question(
    "lease_lost",
    "save_anyway",
    ["skip", "close_tab"],
    {
      save: { label: "gavion.dialer.states.save_anyway" },
      title: { label: "gavion.dialer.states.lease_lost_title" },
    },
    { title: { label: "gavion.dialer.states.lease_lost_title" } },
  ),
  question("crash", "to_complete", ["lost", "dnc"], {
    minutes: UNLOGGED_AFTER_MINUTES,
    list: { label: "gavion.dialer.todo.title" },
  }),
  question("wrong_outcome", "edit", ["call_again", "impossible"], {
    hours: EDIT_WINDOW_MS / 3_600_000,
    action: { label: "gavion.dialer.timeline.edit_outcome" },
  }),
  question("locked", "dont_call", ["call_anyway", "dnc_list"]),
  // GAVION small fixes 5 (owner decision 2026-10-14): the CRM records no calls; Granola takes the notes.
  question("recording", "no_recording", ["read_notice", "yes_quality"], {
    toggle: { label: "gavion.softphone.granola_always" },
  }),
];
